/**
 * CookieBanner · Compact, premium, minimal
 * ---------------------------------------------------------------------------
 * A slim bottom-right toast (max ~380px wide) with subtle neon accents.
 * Not a full-width bar. Not a shouty panel. Not the terminal-brackets aesthetic.
 * Just: clean type, tight spacing, quiet borders, small buttons.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const KEY = 'eh_cookies_v1';

const CookieBanner = () => {
  const [choice, setChoice] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { return null; }
  });
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: false, marketing: false });

  // 900ms grace period so the banner slides in gently after boot animations settle.
  useEffect(() => {
    if (choice) return;
    const t = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(t);
  }, [choice]);

  const commit = (v) => {
    const payload = { ...v, at: new Date().toISOString(), v: 1 };
    try { localStorage.setItem(KEY, JSON.stringify(payload)); } catch { /* noop */ }
    setChoice(payload);
    setVisible(false);
    try { window.dispatchEvent(new CustomEvent('eh:cookies', { detail: payload })); } catch { /* noop */ }
  };

  if (choice || !visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="cb-wrap"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="cb-card">
        <div className="cb-head">
          <div className="cb-dot" aria-hidden />
          <div className="cb-title">Cookies</div>
        </div>

        {!managing ? (
          <p className="cb-body">
            We use cookies to keep sessions secure and improve the site.{' '}
            <Link to="/privacy" className="cb-link">Learn more</Link>
          </p>
        ) : (
          <div className="cb-prefs">
            {[
              { k: 'essential', label: 'Essential', desc: 'Auth & security', locked: true },
              { k: 'analytics', label: 'Analytics', desc: 'Anonymous usage' },
              { k: 'marketing', label: 'Marketing', desc: 'Personalisation' },
            ].map(row => (
              <label key={row.k} className={`cb-row ${row.locked ? 'locked' : ''}`}>
                <input
                  type="checkbox"
                  checked={prefs[row.k]}
                  disabled={row.locked}
                  onChange={e => setPrefs(p => ({ ...p, [row.k]: e.target.checked }))}
                />
                <span className="cb-row-txt">
                  <span className="cb-row-label">{row.label}</span>
                  <span className="cb-row-desc">{row.desc}</span>
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="cb-actions">
          {!managing && (
            <button onClick={() => setManaging(true)} className="cb-btn cb-btn-quiet" data-testid="cookie-manage">
              Manage
            </button>
          )}
          <button
            onClick={() => commit({ essential: true, analytics: false, marketing: false, accepted: false })}
            className="cb-btn cb-btn-quiet"
            data-testid="cookie-reject"
          >
            Reject
          </button>
          <button
            onClick={() => commit(managing ? { ...prefs, accepted: true } : { essential: true, analytics: true, marketing: true, accepted: true })}
            className="cb-btn cb-btn-primary"
            data-testid="cookie-accept"
          >
            {managing ? 'Save' : 'Accept'}
          </button>
        </div>
      </div>

      <style>{`
        .cb-wrap {
          position: fixed;
          right: 16px;
          bottom: 16px;
          left: 16px;
          z-index: 60;
          display: flex;
          justify-content: flex-end;
          pointer-events: none;
          animation: cb-slide .35s cubic-bezier(.2,.9,.3,1) both;
        }
        @keyframes cb-slide {
          from { transform: translateY(14px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .cb-card {
          pointer-events: auto;
          width: 100%;
          max-width: 380px;
          background: rgba(6, 9, 7, 0.92);
          backdrop-filter: blur(18px) saturate(150%);
          -webkit-backdrop-filter: blur(18px) saturate(150%);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 14px;
          padding: 14px 14px 12px;
          box-shadow:
            0 20px 40px -20px rgba(0,0,0,0.6),
            0 0 0 1px rgba(0,255,157,0.06) inset;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .cb-head {
          display: flex; align-items: center; gap: 8px;
          margin-bottom: 6px;
        }
        .cb-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #00ff9d;
          box-shadow: 0 0 8px rgba(0,255,157,0.7);
        }
        .cb-title {
          font-size: 12px; font-weight: 700; letter-spacing: 0.02em;
          color: rgba(255,255,255,0.92);
        }
        .cb-body {
          font-size: 12.5px; line-height: 1.5;
          color: rgba(255,255,255,0.68);
          margin: 0 0 12px;
        }
        .cb-link {
          color: #00ff9d; text-decoration: none;
          border-bottom: 1px dotted rgba(0,255,157,0.5);
          transition: color .15s;
        }
        .cb-link:hover { color: #7cffcc; }
        .cb-prefs { margin: 0 0 10px; display: flex; flex-direction: column; gap: 8px; }
        .cb-row {
          display: flex; align-items: flex-start; gap: 8px;
          font-size: 12px; cursor: pointer;
          color: rgba(255,255,255,0.85);
        }
        .cb-row.locked { opacity: 0.7; cursor: default; }
        .cb-row input[type="checkbox"] {
          margin-top: 2px;
          accent-color: #00ff9d;
          width: 12px; height: 12px;
        }
        .cb-row-txt { display: flex; flex-direction: column; line-height: 1.35; }
        .cb-row-label { font-weight: 600; font-size: 12px; }
        .cb-row-desc { font-size: 10.5px; color: rgba(255,255,255,0.5); }
        .cb-actions {
          display: flex; align-items: center; justify-content: flex-end;
          gap: 6px;
        }
        .cb-btn {
          border: none; cursor: pointer;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 11.5px;
          font-weight: 600;
          font-family: inherit;
          letter-spacing: 0.01em;
          transition: background .15s, color .15s, transform .12s, border-color .15s;
        }
        .cb-btn:active { transform: translateY(1px); }
        .cb-btn-quiet {
          background: transparent;
          color: rgba(255,255,255,0.62);
        }
        .cb-btn-quiet:hover { color: rgba(255,255,255,0.92); background: rgba(255,255,255,0.04); }
        .cb-btn-primary {
          background: #00ff9d;
          color: #041008;
          box-shadow: 0 4px 14px -6px rgba(0,255,157,0.65);
        }
        .cb-btn-primary:hover { background: #7cffcc; }

        @media (max-width: 480px) {
          .cb-wrap { left: 12px; right: 12px; bottom: 12px; justify-content: stretch; }
          .cb-card { max-width: none; }
        }
      `}</style>
    </div>
  );
};

export default CookieBanner;
